import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer, inject } from 'mobx-react';

import Tab from '@material-ui/core/Tab';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';
import createStyles from '@material-ui/core/styles/createStyles';
import Button from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';
import { CircularProgress } from '@material-ui/core';

import compose from 'helpers/compose';
import Tabs from 'components/CustomTabs/CustomTabs';

import { RootStore } from 'stores/rootStore';
import { DepositMoneyStore } from 'stores/account/depositMoneyStore';
import { Currency, AccountMode, EdwardColors } from 'enums';
import { EmployerExt } from 'stores/workflowStores/finances/employers/model';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    employer: {
      background: EdwardColors.GRAY_EXTRALIGHT,
      width: '100%',
      padding: theme.spacing(1),
    },
    divider: {
      padding: theme.spacing(1, 0),
    },
    label: {
      width: '50%',
      minWidth: '50%',
      [theme.breakpoints.down('xs')]: {
        '& > p': { fontSize: '0.9em' },
      },
    },
    value: {
      width: '50%',
      minWidth: '50%',
    },
    qrBig: {
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    qrSmall: {
      paddingTop: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
      },
    },
    download: {
      display: 'flex',
      padding: theme.spacing(4, 0),
    },
  }),
);

interface Props {
  rootStore: RootStore;
  openDeposit: Function;
  accountId: string;
  personalNumber?: string;
  accountTitle?: string;
  currency: Currency;
  mode: AccountMode;
}

const DepositInstructions: React.FC<Props> = ({
  rootStore,
  openDeposit,
  accountId,
  currency,
  mode,
  personalNumber,
  accountTitle
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [store] = useState(new DepositMoneyStore(rootStore, accountId));
  const { data: depositInstruction } = store;
  const [tab, setTab] = useState<'client' | 'employer'>('client');

  useEffect(() => {
    store.fetchDeposit();
  }, [accountId, store]);

  const handleTabChange = (_event: React.ChangeEvent<{}>, value?: 'client' | 'employer') => {
    if (value !== undefined) {
      setTab(value);
    }
  };

  const renderDepositInfo = (
    type: 'client' | 'employer' | 'employee',
    employer?: EmployerExt,
    personalNumber?: string,
  ) => {
    return (
      <>
        <Grid container>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              <strong>
                {t(
                  type === 'employer'
                    ? 'accountDetail_moneyDepositInstruction_instructionsEmployer'
                    : type === 'employee'
                    ? 'accountDetail_moneyDepositInstruction_instructionsEmployee'
                    : 'accountDetail_moneyDepositInstruction_instructions',
                )}
              </strong>
            </Typography>
          </Grid>
        </Grid>

        <Grid container spacing={0} className={classes.divider}>
          <Grid item xs={12}>
            <Divider />
          </Grid>
        </Grid>

        <Grid container alignItems="center" key={employer?.employer.id || 'client'}>
          <Grid container item xs={12} md={type === 'client' ? 12 : 8}>
            {currency === Currency.CZK && (
              <>
                <Grid item className={classes.label}>
                  <Typography>{t('accountDetail_moneyDepositInstruction_accountNumberText')}:</Typography>
                </Grid>
                <Grid item className={classes.value}>
                  <Typography>
                    <strong>{depositInstruction && depositInstruction.targetAccount}</strong>
                  </Typography>
                </Grid>
                <Grid item className={classes.label}>
                  <Typography>{t('accountDetail_moneyDepositInstruction_accountNumberTextEUR')}:</Typography>
                </Grid>
                <Grid item className={classes.value}>
                  <Typography>{depositInstruction && depositInstruction.targetAccountEur}</Typography>
                </Grid>
              </>
            )}
            {currency === Currency.EUR && (
              <>
                <Grid item className={classes.label}>
                  <Typography>{t('accountDetail_moneyDepositInstruction_accountNumberTextEUR')}:</Typography>
                </Grid>
                <Grid item className={classes.value}>
                  <Typography>
                    <strong>{depositInstruction && depositInstruction.targetAccount}</strong>
                  </Typography>
                </Grid>
              </>
            )}
            <Grid item className={classes.label}>
              <Typography>{t('accountDetail_moneyDepositInstruction_targetAccountEurIban')}:</Typography>
            </Grid>
            <Grid item className={classes.value}>
              <Typography>{depositInstruction && depositInstruction.targetAccountEurIban}</Typography>
            </Grid>

            <Grid item className={classes.label}>
              <Typography>{t('accountDetail_moneyDepositInstruction_targetAccountEurSwift')}:</Typography>
            </Grid>
            <Grid item className={classes.value}>
              <Typography>{depositInstruction && depositInstruction.targetAccountEurSwift}</Typography>
            </Grid>

            <Grid item className={classes.label}>
              <Typography>{t('accountDetail_moneyDepositInstruction_variableSymbol')}:</Typography>
            </Grid>
            <Grid item className={classes.value}>
              <Typography>
                <strong>{depositInstruction && depositInstruction.variableSymbol}</strong>
              </Typography>
            </Grid>
            {(type === 'employer' || type === 'employee') && employer && (
              <>
                <Grid item className={classes.label}>
                  <Typography>{t('accountDetail_moneyDepositInstruction_specificSymbol')}:</Typography>
                </Grid>
                <Grid item className={classes.value}>
                  <Typography>
                    <strong>
                      {type === 'employer' ? employer.employer.companyNumber : personalNumber?.replace('/', '')}
                    </strong>
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
          {(type === 'employer' || type === 'employee') && (
            <Grid container item xs={12} md={4} style={{ justifyContent: 'center', paddingTop: '16px' }}>
              <div style={{ width: '100%', textAlign: 'center' }}>{t(`accountDetail_moneyDepositInstruction_QR_${currency}`)}</div>
              {type === 'employer' && <img src={`data:image/png;base64,${employer?.qr}`} alt="QR" />}
              {type === 'employee' && <img src={`data:image/png;base64,${employer?.qrClient}`} alt="QR" />}
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  const renderDownload = (employer: EmployerExt) => {
    return (
      <Grid container spacing={0}>
        <Grid item xs={12} alignItems="center" justifyContent="center" className={classes.download}>
          <Button
            startIcon={
              store.pdfLoading[employer.employer.companyNumber] ? <CircularProgress size={20} /> : <GetAppIcon />
            }
            onClick={() => store.getEmployerPayment(employer.employer.companyNumber)}
          >
            {t('general_downloadPDF')}
          </Button>
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog maxWidth="lg" open={true} aria-labelledby="form-dialog-title" fullWidth>
      <DialogHeader onClose={() => openDeposit(false)}>{t('accountDetail_moneyDepositInstruction_title')} {accountTitle}</DialogHeader>
      <DialogContent>
        {mode === AccountMode.LTIP && depositInstruction?.employers && depositInstruction?.employers?.length > 0 && (
          <Tabs value={tab} onChange={handleTabChange} fullWidth>
            <Tab value="client" label={t('general_client')} />
            <Tab value="employer" label={t('general_employer')} />
          </Tabs>
        )}

        <Grid container>
          <Grid container item xs={12} md={tab === 'client' ? 8 : 12}>
            {tab === 'client'
              ? renderDepositInfo('client')
              : depositInstruction?.employers &&
                depositInstruction.employers.map((employer) => (
                  <>
                    <Typography variant="h3" className={classes.employer}>
                      {t('employer')}: {employer.employer.companyName}
                    </Typography>
                    {renderDepositInfo('employer', employer)}
                    <Grid item xs={12} spacing={2}>
                      &nbsp;
                    </Grid>
                    {personalNumber && renderDepositInfo('employee', employer, personalNumber)}
                    {renderDownload(employer)}
                    <Grid item xs={12} spacing={2}>
                      &nbsp;
                    </Grid>
                  </>
                ))}

            {tab === 'client' && (
              <Grid
                container
                item
                xs={12}
                md={4}
                alignItems="center"
                alignContent="center"
                direction="column"
                className={classes.qrSmall}
              >
                {t(`accountDetail_moneyDepositInstruction_QR_${currency}`)}
                {depositInstruction?.qr && <img src={`data:image/png;base64,${depositInstruction.qr}`} alt="QR" />}
              </Grid>
            )}
            {tab === 'client' && (
              <>
                <Grid container>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </Grid>

                <Grid container alignItems="center">
                  {depositInstruction &&
                    depositInstruction.sourceAccounts.map((account, i) => {
                      return (
                        <React.Fragment key={account.fullNumber}>
                          <Grid item className={classes.label}>
                            <Typography>
                              {t(`accountDetail_moneyDepositInstruction_sendMoneyFrom${i === 0 ? 'Main' : ''}`)}:
                            </Typography>
                          </Grid>
                          <Grid item className={classes.value}>
                            <Typography>
                              {i === 0 ? <strong>{account.fullNumber}</strong> : account.fullNumber} ({account.currency}
                              )
                            </Typography>
                          </Grid>
                        </React.Fragment>
                      );
                    })}
                </Grid>

                <Grid container>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
          {tab === 'client' && (
            <Grid
              container
              item
              xs={12}
              md={4}
              alignItems="center"
              alignContent="center"
              direction="column"
              className={classes.qrBig}
            >
              {t(`accountDetail_moneyDepositInstruction_QR_${currency}`)}
              {depositInstruction?.qr && <img src={`data:image/png;base64,${depositInstruction.qr}`} alt="QR" />}
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default compose(inject('rootStore'), observer)(DepositInstructions);
